import { Menu } from '@base-ui/react/menu';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

import { clearStoredActiveOrgSlug, setStoredActiveOrgSlug, useActiveOrg } from '../active-org.ts';
import { clearStoredActiveRepo, useActiveRepo } from '../active-repo.ts';
import { useAuth, useRepositories } from '../api.ts';

import styles from './OrgSwitcher.module.css';

interface OrgSwitcherProps {
  email: string;
  signOutUrl: string;
}

/**
 * Bottom-of-rail org switcher + account menu. The circular trigger shows
 * the active org's icon (the GitHub/GitLab owner avatar captured at link
 * time, or a colored initial when none is available). The menu lets the
 * user:
 *
 *   • see who they're signed in as and jump to Profile / Team,
 *   • toggle the active organization (text list, active one highlighted),
 *   • filter to a single repo within the active org (nested under it),
 *   • sign out.
 *
 * Org switching reuses the localStorage active-org seam (`active-org.ts`)
 * so every org-scoped query re-keys reactively; the repo filter is the
 * org-scoped companion in `active-repo.ts`. Switching orgs clears any
 * repo filter, since a repo id only means something inside its org.
 *
 * (The account items — email, Profile, Team, Sign out — used to live in a
 * separate menu hanging off the logo; they've been merged in here so the
 * logo can go back to being a static link home.)
 */
const ACCOUNT_LINKS = [
  { to: '/settings/profile', label: 'Profile' },
  { to: '/settings/team', label: 'Team' },
] as const;

export function OrgSwitcher({ email, signOutUrl }: OrgSwitcherProps) {
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);
  const { activeRepoId, selectRepo, clearRepo } = useActiveRepo();

  // Repos for the active org power both the nested filter list and (as a
  // fallback) the trigger icon. A user-uploaded org icon takes precedence
  // over the first repo's owner avatar.
  const reposQuery = useRepositories(active?.slug ?? null);
  const repos = reposQuery.data ?? [];
  const orgAvatar = active?.icon ?? repos.find((r) => r.avatar_url)?.avatar_url ?? null;

  const switchOrg = useCallback(
    (slug: string) => {
      if (slug === active?.slug) return;
      clearStoredActiveRepo();
      setStoredActiveOrgSlug(slug);
    },
    [active?.slug],
  );

  // Nothing to switch between / no org resolved yet.
  if (!active || orgs.length === 0) return null;

  const initial = active.name.charAt(0).toUpperCase() || '?';

  return (
    <Menu.Root>
      <Menu.Trigger
        className={styles.trigger}
        aria-label='Switch organization or filter repositories'
      >
        {orgAvatar ? (
          <img src={orgAvatar} alt='' className={styles.avatarImg} />
        ) : (
          <span className={styles.avatarFallback}>{initial}</span>
        )}
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={8} side='right' align='end'>
          <Menu.Popup className={styles.popup}>
            <div className={styles.email}>{email}</div>
            {ACCOUNT_LINKS.map(({ to, label }) => (
              <Menu.Item key={to} className={styles.accountItem} render={<Link to={to} />}>
                {label}
              </Menu.Item>
            ))}
            <Menu.Separator className={styles.separator} />
            {orgs.map((o) => {
              const isActive = o.slug === active.slug;
              return (
                <div key={o.slug}>
                  <Menu.Item
                    className={`${styles.orgItem}${isActive ? ` ${styles.orgItemActive}` : ''}`}
                    // Keep the menu open on switch so the newly active
                    // org's repos render inline (one fewer reopen).
                    closeOnClick={false}
                    onClick={() => switchOrg(o.slug)}
                  >
                    {o.name}
                  </Menu.Item>
                  {isActive ? (
                    <div className={styles.repoGroup}>
                      <Menu.Item
                        className={`${styles.repoItem}${!activeRepoId ? ` ${styles.repoItemActive}` : ''}`}
                        onClick={() => clearRepo()}
                      >
                        All repos
                      </Menu.Item>
                      {repos.map((r) => (
                        <Menu.Item
                          key={r.id}
                          className={`${styles.repoItem}${activeRepoId === r.id ? ` ${styles.repoItemActive}` : ''}`}
                          onClick={() => selectRepo(r.id)}
                        >
                          {r.name}
                        </Menu.Item>
                      ))}
                      {reposQuery.isPending ? (
                        <div className={styles.repoHint}>Loading…</div>
                      ) : repos.length === 0 ? (
                        <div className={styles.repoHint}>No repos yet</div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
            <Menu.Separator className={styles.separator} />
            <Menu.Item
              className={styles.accountItem}
              // Forget the active org/repo before the redirect so the next
              // sign-in's org pick isn't shadowed by this session's choice.
              onClick={() => {
                clearStoredActiveOrgSlug();
                clearStoredActiveRepo();
              }}
              render={<a href={signOutUrl} />}
            >
              Sign out
            </Menu.Item>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
